import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { getUser, updateUser, grantFreePro, getUserUsageStats, listUserDatasets, deleteDataset, getDatasetPreview, copyDatasetToMyAccount, type DatasetPreview } from '../services/apiService';
import SubscriptionEditor from './SubscriptionEditor';
import { useCachedData } from '../hooks/useCachedData';
import { Button, Modal } from './ui';

interface User {
  userId: string;
  email: string;
  subscription: any;
  datasets?: any[];
  [key: string]: any;
}

interface UserDetailProps {
  user: User;
  onClose: () => void;
  onUpdate: (user: User) => void;
}

function DatasetPreviewContent({
  preview,
  formatDate,
}: {
  preview: DatasetPreview;
  formatDate: (iso: string | null) => string;
}) {
  const { taskCount, swimlanes, startDate, endDate, tasksPreview } = preview;
  const moreCount = taskCount > tasksPreview.length ? taskCount - tasksPreview.length : 0;

  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-secondary">
        {taskCount} task{taskCount !== 1 ? 's' : ''}
        {startDate || endDate ? ` · ${formatDate(startDate)} – ${formatDate(endDate)}` : ''}
        {swimlanes.length > 0 ? ` · Swimlanes: ${swimlanes.join(', ')}` : ''}
      </p>
      <div className="overflow-x-auto max-h-64 overflow-y-auto rounded-xl border border-neutral-border bg-white">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 bg-neutral-background border-b border-neutral-border">
            <tr>
              <th className="text-left py-2 px-3 font-medium text-neutral-secondary">Task</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-secondary">Start</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-secondary">End</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-secondary">Swimlane</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-secondary">Milestone</th>
              <th className="text-left py-2 px-3 font-medium text-neutral-secondary">Progress</th>
            </tr>
          </thead>
          <tbody>
            {tasksPreview.map((task, i) => (
              <tr key={i} className="border-b border-neutral-border/50 last:border-0">
                <td className="py-1.5 px-3 text-neutral-primary">{task.name}</td>
                <td className="py-1.5 px-3 text-neutral-secondary">{formatDate(task.startDate)}</td>
                <td className="py-1.5 px-3 text-neutral-secondary">{formatDate(task.endDate)}</td>
                <td className="py-1.5 px-3 text-neutral-secondary">{task.swimlane ?? '—'}</td>
                <td className="py-1.5 px-3 text-neutral-secondary">{task.isMilestone ? 'Yes' : '—'}</td>
                <td className="py-1.5 px-3 text-neutral-secondary">{task.progress != null ? `${task.progress}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {moreCount > 0 && (
        <p className="text-xs text-neutral-secondary">… and {moreCount} more task{moreCount !== 1 ? 's' : ''}</p>
      )}
    </div>
  );
}

export default function UserDetail({ user: initialUser, onClose, onUpdate }: UserDetailProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'subscription' | 'usage' | 'datasets'>('overview');
  const [user, setUser] = useState<User>(initialUser);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [success]);

  // Load full user data - use useCallback to stabilize the fetch function
  const fetchUserData = useCallback(async () => {
    const result = await getUser(user.userId);
    return result;
  }, [user.userId]);

  const { data: fullUserData, loading: loadingUser } = useCachedData<{ user: User }>(
    `user_${user.userId}`,
    fetchUserData
  );

  // Load usage stats - use useCallback to stabilize the fetch function
  const fetchUsageStats = useCallback(async () => {
    const result = await getUserUsageStats(user.userId);
    return result;
  }, [user.userId]);

  const { data: usageStats, loading: loadingUsage } = useCachedData<{ userId: string; stats: any }>(
    `user_usage_${user.userId}`,
    fetchUsageStats
  );

  // Load datasets - use useCallback to stabilize the fetch function
  const fetchDatasets = useCallback(async () => {
    const result = await listUserDatasets(user.userId);
    return result;
  }, [user.userId]);

  const { data: datasetsData, loading: loadingDatasets, refresh: refreshDatasets } = useCachedData<{ datasets: any[] }>(
    `user_datasets_${user.userId}`,
    fetchDatasets
  );

  useEffect(() => {
    if (fullUserData?.user) {
      setUser(fullUserData.user);
    }
  }, [fullUserData]);

  const handleUpdate = async (updates: any) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await updateUser(user.userId, updates);
      if (result.success) {
        setUser(result.user);
        onUpdate(result.user);
        setSuccess('Saved');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantFreePro = async (expirationDate?: string | null) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await grantFreePro(user.userId, expirationDate);
      if (result.success) {
        setUser(result.user);
        onUpdate(result.user);
        setSuccess('Free Pro granted');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to grant free pro');
    } finally {
      setLoading(false);
    }
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteDatasetId, setDeleteDatasetId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [copyDatasetId, setCopyDatasetId] = useState<string | null>(null);

  const openDeleteConfirm = (datasetId: string) => {
    setDeleteDatasetId(datasetId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDatasetId) return;
    try {
      await deleteDataset(deleteDatasetId);
      setDeleteConfirmOpen(false);
      setDeleteDatasetId(null);
      refreshDatasets();
    } catch (err: any) {
      setDeleteConfirmOpen(false);
      setDeleteDatasetId(null);
      setDeleteError(err.message || 'Failed to delete dataset');
    }
  };

  const [expandedPreviewDatasetId, setExpandedPreviewDatasetId] = useState<string | null>(null);
  const [previewCache, setPreviewCache] = useState<Record<string, DatasetPreview>>({});
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [copyingDatasetId, setCopyingDatasetId] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const loadPreview = useCallback(async (datasetId: string) => {
    if (previewCache[datasetId]) return;
    setLoadingPreviewId(datasetId);
    setPreviewError(null);
    try {
      const { preview } = await getDatasetPreview(datasetId);
      setPreviewCache((prev) => ({ ...prev, [datasetId]: preview }));
    } catch (err: any) {
      setPreviewError(err.message || 'Failed to load preview');
    } finally {
      setLoadingPreviewId(null);
    }
  }, [previewCache]);

  const handleTogglePreview = (datasetId: string) => {
    if (expandedPreviewDatasetId === datasetId) {
      setExpandedPreviewDatasetId(null);
      setPreviewError(null);
      return;
    }
    setExpandedPreviewDatasetId(datasetId);
    setPreviewError(null);
    loadPreview(datasetId);
  };

  const openCopyConfirm = (datasetId: string) => {
    setCopyDatasetId(datasetId);
    setCopyConfirmOpen(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyDatasetId) return;
    setCopyConfirmOpen(false);
    const idToCopy = copyDatasetId;
    setCopyDatasetId(null);
    setCopyingDatasetId(idToCopy);
    setCopySuccess(null);
    setError(null);
    try {
      const { id, name } = await copyDatasetToMyAccount(idToCopy);
      setCopySuccess(`Dataset copied as "${name}" (ID: ${id})`);
      setTimeout(() => setCopySuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to copy dataset');
    } finally {
      setCopyingDatasetId(null);
    }
  };

  const formatPreviewDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  if (loadingUser) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-modal max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex justify-between items-center p-6 border-b border-neutral-border">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-9 w-9 rounded-lg bg-gray-100" />
          </div>
          <div className="flex border-b border-neutral-border gap-6 px-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 w-16 bg-gray-100 rounded mt-0.5 animate-pulse" />
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-4 w-full max-w-sm bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition-opacity duration-200">
      <div className="bg-white rounded-2xl shadow-modal max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-all duration-200">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-neutral-border">
          <h2 className="text-2xl font-bold text-neutral-primary">{user.email}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-border/50 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:ring-offset-1"
          >
            <X className="w-5 h-5 text-neutral-secondary" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-border px-2">
          {(['overview', 'subscription', 'usage', 'datasets'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-neutral-secondary hover:text-neutral-primary'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="rounded-2xl border border-neutral-border bg-white p-6 shadow-card">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                {[
                  { label: 'User ID', value: user.userId },
                  { label: 'Email', value: user.email },
                  { label: 'Email Verified', value: user.emailVerified ? 'Yes' : 'No' },
                  {
                    label: 'Subscription Tier',
                    value:
                      (user.subscription?.tier || 'trial') === 'pro'
                        ? user.subscription?.stripeSubscriptionId
                          ? 'Pro (Subscription)'
                          : 'Pro (Gift)'
                        : 'Trial',
                  },
                  { label: 'Subscription Status', value: user.subscription?.status || 'active' },
                  { label: 'AI Used', value: String(user.aiUsed ?? 0) },
                  { label: 'AI Limit', value: String(user.subscription?.aiLimit ?? 100) },
                ].map((row) => (
                  <div key={row.label}>
                    <dt className="text-sm font-medium text-neutral-secondary">{row.label}</dt>
                    <dd className="mt-0.5 text-sm font-medium text-neutral-primary">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {activeTab === 'subscription' && (
            <SubscriptionEditor
              user={user}
              onUpdate={handleUpdate}
              onGrantFreePro={handleGrantFreePro}
              loading={loading}
            />
          )}

          {activeTab === 'usage' && (
            <div>
              {loadingUsage ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent mx-auto" />
                  <p className="mt-2 text-sm text-neutral-secondary">Loading usage stats...</p>
                </div>
              ) : usageStats?.stats ? (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Total Interactions', value: usageStats.stats.totalInteractions },
                    { label: 'Total Tokens', value: usageStats.stats.totalTokens.toLocaleString() },
                    { label: 'Input Tokens', value: usageStats.stats.totalInputTokens.toLocaleString() },
                    { label: 'Output Tokens', value: usageStats.stats.totalOutputTokens.toLocaleString() },
                    { label: 'Total Cost', value: `$${usageStats.stats.totalCost.toFixed(2)}` },
                    { label: 'Overage Interactions', value: usageStats.stats.overageInteractions },
                  ].map((stat) => (
                    <div key={stat.label} className="rounded-2xl border border-neutral-border bg-white p-5 shadow-card">
                      <p className="text-sm font-medium text-neutral-secondary">{stat.label}</p>
                      <p className="mt-1 text-2xl font-bold text-neutral-primary">{stat.value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-secondary">No usage stats available</p>
              )}
            </div>
          )}

          {activeTab === 'datasets' && (
            <div>
              {copySuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <p className="text-sm text-green-800">{copySuccess}</p>
                </div>
              )}
              {previewError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-sm text-red-800">{previewError}</p>
                </div>
              )}
              {loadingDatasets ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent mx-auto" />
                  <p className="mt-2 text-sm text-neutral-secondary">Loading datasets...</p>
                </div>
              ) : datasetsData?.datasets && datasetsData.datasets.length > 0 ? (
                <div className="rounded-2xl border border-neutral-border bg-white shadow-card overflow-hidden divide-y divide-neutral-border">
                  {datasetsData.datasets.map((dataset) => (
                    <div key={dataset.id}>
                      <div className="flex flex-wrap items-center gap-2 px-5 py-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-neutral-primary">{dataset.name}</p>
                          <p className="text-sm text-neutral-secondary mt-0.5">
                            Created: {dataset.createdAt ? new Date(dataset.createdAt).toLocaleDateString() : 'Unknown'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            onClick={() => handleTogglePreview(dataset.id)}
                          >
                            {expandedPreviewDatasetId === dataset.id ? 'Hide preview' : 'Preview'}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => openCopyConfirm(dataset.id)}
                            disabled={copyingDatasetId === dataset.id}
                          >
                            {copyingDatasetId === dataset.id ? 'Copying…' : 'Copy to my account'}
                          </Button>
                          <Button variant="danger" onClick={() => openDeleteConfirm(dataset.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                      {expandedPreviewDatasetId === dataset.id && (
                        <div className="border-t border-neutral-border bg-neutral-background/50 px-5 py-4">
                          {loadingPreviewId === dataset.id ? (
                            <div className="flex items-center gap-2 text-sm text-neutral-secondary">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-primary border-t-transparent" />
                              Loading preview…
                            </div>
                          ) : previewCache[dataset.id] ? (
                            <DatasetPreviewContent
                              preview={previewCache[dataset.id]}
                              formatDate={formatPreviewDate}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-secondary">No datasets found</p>
              )}
            </div>
          )}
        </div>
      </div>
      <Modal
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setDeleteDatasetId(null); }}
        message="Are you sure you want to delete this dataset?"
        variant="confirm"
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
      />
      <Modal
        open={Boolean(deleteError)}
        onClose={() => setDeleteError(null)}
        message={deleteError ?? ''}
        variant="alert"
      />
      <Modal
        open={copyConfirmOpen}
        onClose={() => { setCopyConfirmOpen(false); setCopyDatasetId(null); }}
        message="Copy this dataset under your user for debugging?"
        variant="confirm"
        confirmLabel="Copy"
        onConfirm={handleCopyConfirm}
      />
    </div>
  );
}
